import { PhotoResponseDto, UserLoginResDto } from '@docbay/schemas';
import { parseJwt } from 'common/helpers/parseJwt';
import { StorageFields } from 'application/constants';

export enum AuthLocalStorage {
  accessToken = 'accessToken',
  refreshToken = 'refreshToken',
  accessTokenExpiresAt = 'accessTokenExpiresAt',
  profiles = 'profiles',
}

export interface Profile {
  token: string;
  refreshToken: string;
  email: string;
  firstName: string;
  lastName: string;
  photo: PhotoResponseDto;
  id: string;
  userTypes?: string;
}
/**
 * Sets the authentication session in local storage.
 * @param value - The UserLoginResDto object containing the token and refresh token information.
 */
export const setUsersAuthSessionStorage = (value: UserLoginResDto) => {
  localStorage.setItem(
    AuthLocalStorage.accessToken,
    JSON.stringify(value.token),
  );
  localStorage.setItem(
    AuthLocalStorage.refreshToken,
    JSON.stringify(value.refreshToken),
  );

  localStorage.setItem(AuthLocalStorage.accessTokenExpiresAt, value.expiresAt);

  const token = parseJwt(value.token);

  localStorage.setItem(StorageFields.userRole, token['custom:userTypes']);
  localStorage.setItem(StorageFields.userId, value.user?.id as string);
  localStorage.setItem(
    StorageFields.userPermissions,
    token['custom:permissions'],
  );

  if (token && token['custom:proPermissions']?.length) {
    localStorage.setItem(
      StorageFields.userProPermissions,
      JSON.stringify(token['custom:proPermissions']),
    );
  }
};

/**
 * Sets the profiles in local storage.
 * @param profiles - An array of Profile objects to be stored.
 */
export const setProfilesInSessionStorage = (profiles: Profile[]) => {
  const parsedProfile = profiles.map((item) => {
    return {
      ...item,
      userTypes: parseJwt(profiles[0].token)
        ? parseJwt(profiles[0].token)['custom:userTypes']
        : '',
    };
  });

  localStorage.setItem(
    AuthLocalStorage.profiles,
    JSON.stringify(parsedProfile),
  );
};

/**
 * Retrieves the profiles from local storage.
 * @returns An array of Profile objects, or null if no profiles are found.
 */
export const getProfilesFromSessionStorage = (): Profile[] | null => {
  const profiles = localStorage.getItem(AuthLocalStorage.profiles);

  return profiles ? (JSON.parse(profiles) as Profile[]) : null;
};

/**
 * Retrieves the user role from local storage.
 * @returns The user role, or null if not found.
 */
export const getUserRole = () => localStorage.getItem(StorageFields.userRole);
/**
 * Retrieves the user ID from local storage.
 * @returns The user ID, or null if not found.
 */
export const getUserId = () => localStorage.getItem(StorageFields.userId);

/**
 * Retrieves the access token from local storage.
 * @returns The access token, or an empty string if not found.
 */
export const getToken = () => {
  const token = localStorage.getItem(AuthLocalStorage.accessToken);
  return token ? JSON.parse(token) : '';
};

/**
 * Sets the refresh token session in local storage.
 * @param value - An object containing the new token and expiration time.
 */
export const setUsersRefreshTokenSessionStorage = (value: {
  token: string;
  expiresAt: string;
}) => {
  localStorage.setItem(
    AuthLocalStorage.accessToken,
    JSON.stringify(value.token),
  );

  localStorage.setItem(AuthLocalStorage.accessTokenExpiresAt, value.expiresAt);
};
